import { useState, FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Loader2, Eye, EyeOff, CheckCircle } from 'lucide-react';
import LogoIcon from '../components/LogoIcon';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';

export default function RegisterPage() {
    const { register } = useAuth();
    const navigate = useNavigate();

    const [name, setName] = useState('');
    const [company, setCompany] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPw, setShowPw] = useState(false);
    const [loading, setLoading] = useState(false);

    const pwStrong = password.length >= 8;

    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault();
        if (!email || !password || !pwStrong) return;
        setLoading(true);
        try {
            await register(email, password, name || undefined, company || undefined);
            navigate('/dashboard', { replace: true });
        } catch (err: unknown) {
            const msg = (err as { response?: { data?: { error?: string } } })
                ?.response?.data?.error || '注册失败，请稍后重试';
            toast.error(msg);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-surface-0 px-4">
            <div className="w-full max-w-sm">
                {/* Logo */}
                <div className="flex flex-col items-center mb-8">
                    <div className="mb-4 shadow-lg shadow-brand-900/50 rounded-2xl">
                        <LogoIcon size={48} />
                    </div>
                    <h1 className="text-2xl font-bold text-white">税码通</h1>
                    <p className="text-slate-500 text-sm mt-1">AI 智能 HTS 编码分类平台</p>
                </div>

                <div className="card">
                    <h2 className="text-lg font-semibold text-white mb-1">创建账户</h2>
                    <p className="text-xs text-slate-500 mb-6">免费开始，随时升级</p>

                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div>
                            <label className="block text-xs font-medium text-slate-400 mb-1.5">
                                公司 / 店铺名
                                <span className="text-slate-600 ml-1">（将作为工作空间名称）</span>
                            </label>
                            <input
                                type="text"
                                value={company}
                                onChange={(e) => setCompany(e.target.value)}
                                placeholder="例：XX 跨境贸易 / 我的亚马逊店铺"
                                className="input-field"
                                autoFocus
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-medium text-slate-400 mb-1.5">
                                姓名 <span className="text-slate-600">（可选）</span>
                            </label>
                            <input
                                type="text"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                placeholder="张三"
                                className="input-field"
                            />
                        </div>

                        <div>
                            <label className="block text-xs font-medium text-slate-400 mb-1.5">
                                邮箱地址
                            </label>
                            <input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="you@example.com"
                                required
                                className="input-field"
                            />
                        </div>

                        <div>
                            <label className="block text-xs font-medium text-slate-400 mb-1.5">
                                密码
                            </label>
                            <div className="relative">
                                <input
                                    type={showPw ? 'text' : 'password'}
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    placeholder="至少 8 位"
                                    required
                                    className="input-field pr-10"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPw((v) => !v)}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
                                >
                                    {showPw ? <EyeOff size={15} /> : <Eye size={15} />}
                                </button>
                            </div>
                            {password.length > 0 && (
                                <div className={`flex items-center gap-1 mt-1.5 text-xs ${pwStrong ? 'text-emerald-400' : 'text-amber-400'}`}>
                                    <CheckCircle size={11} />
                                    {pwStrong ? '密码强度良好' : '密码至少 8 位'}
                                </div>
                            )}
                        </div>

                        <button
                            type="submit"
                            disabled={loading || !email || !pwStrong}
                            className="btn-primary w-full mt-2"
                        >
                            {loading ? (
                                <><Loader2 size={16} className="animate-spin" /> 注册中...</>
                            ) : (
                                '免费注册'
                            )}
                        </button>
                    </form>

                    <p className="text-center text-sm text-slate-500 mt-5">
                        已有账户？{' '}
                        <Link to="/login" className="text-brand-400 hover:text-brand-300 font-medium">
                            立即登录
                        </Link>
                    </p>
                </div>

                {/* Feature highlights */}
                <div className="mt-6 grid grid-cols-2 gap-3 text-xs text-slate-500">
                    {['AI 精准 HTS 分类', '批量 CSV 上传', '关税实时估算', '分类历史记录'].map((f) => (
                        <div key={f} className="flex items-center gap-1.5">
                            <CheckCircle size={11} className="text-emerald-500 flex-shrink-0" />
                            {f}
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}
